import { useEffect, useState } from "react";
import { activate } from "../../../utils/keyboard-activate";
import { useNavigate } from "react-router-dom";
import { getPlayerInteractions } from "../../../services/crmContacts";
import type { PlayerInteractionResponse, PlayerInteractionPage } from "../../../types/crm";
import "./PlayerInteractionLog.css";

interface PlayerInteractionLogProps {
  profileId: string;
}

export const PlayerInteractionLog = ({ profileId }: PlayerInteractionLogProps) => {
  const navigate = useNavigate();
  const [interactions, setInteractions] = useState<PlayerInteractionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data: PlayerInteractionPage = await getPlayerInteractions(profileId);
        setInteractions(data.items);
        setTotalCount(data.totalCount);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [profileId]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="interaction-log__loading">Loading interactions...</div>;
  }

  if (interactions.length === 0) {
    return <div className="interaction-log__empty">No interactions recorded yet.</div>;
  }

  return (
    <div className="interaction-log">
      <div className="interaction-log__header">
        <h3>Interaction Log</h3>
        <span className="interaction-log__count">{totalCount} total</span>
      </div>
      <div className="interaction-log__list">
        {interactions.map((interaction) => (
          <div key={interaction.id} className="interaction-log__item">
            <div className="interaction-log__item-header">
              <span className={`interaction-log__direction interaction-log__direction--${interaction.direction.toLowerCase()}`}>
                {interaction.direction === "Inbound" ? "\u2190 Inbound" : "\u2192 Outbound"}
              </span>
              {interaction.channel && (
                <span className="interaction-log__channel">{interaction.channel}</span>
              )}
              <span className="interaction-log__date">{formatDate(interaction.interactionDate)}</span>
            </div>

            {interaction.interactionType === "BulkOutreach" ? (
              <div className="interaction-log__bulk">
                <div
                  className="interaction-log__bulk-summary"
                  {...activate(() => setExpandedId(expandedId === interaction.id ? null : interaction.id))}
                >
                  <span className="interaction-log__bulk-icon">{expandedId === interaction.id ? "\u25BC" : "\u25B6"}</span>
                  Bulk outreach sent to {interaction.totalRecipients} coach{interaction.totalRecipients !== 1 ? "es" : ""}
                </div>
                {expandedId === interaction.id && interaction.recipients && (
                  <div className="interaction-log__bulk-detail">
                    {interaction.messagePreview && (
                      <div className="interaction-log__message-preview">{interaction.messagePreview}</div>
                    )}
                    <div className="interaction-log__recipients">
                      {interaction.recipients.map((r) => (
                        <div key={r.id} className="interaction-log__recipient">
                          {r.coachId ? (
                            <button
                              type="button"
                              className="interaction-log__recipient-name-link"
                              onClick={() => navigate(`/crm/contacts/${r.coachId}`)}
                            >
                              {r.coachName}
                            </button>
                          ) : (
                            <span>{r.coachName}</span>
                          )}
                          {r.coachSchool && <span className="interaction-log__recipient-school">{r.coachSchool}</span>}
                          {r.coachEmail && <span className="interaction-log__recipient-email">{r.coachEmail}</span>}
                          <span className={`interaction-log__recipient-status interaction-log__recipient-status--${r.status.toLowerCase()}`}>
                            {r.status}
                          </span>
                          {r.firstResponseAt && (
                            <span className="interaction-log__recipient-status interaction-log__recipient-status--responded">
                              Coach Responded
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="interaction-log__message">
                {interaction.senderName && (
                  <span className="interaction-log__sender">{interaction.senderName}</span>
                )}
                {interaction.messagePreview && (
                  <p className="interaction-log__preview">{interaction.messagePreview}</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
